import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageLayout from "../../../shared/ui/layouts/PageLayout/PageLayout";
import { ImmotionAuftragSection } from "../../../features/work-order";
import { useWorkOrderDetail, getWorkOrderStatusLabel, getWorkOrderStatusColor } from "../../../entities/work-order";
import { routes } from "../../../shared/config/routes";
import { AppointmentsSection } from "./ui/AppointmentsSection";
import { AuditLogFeed } from "../../../widgets/audit-log";
import { useAuthStore } from "../../../features/auth/model/store";
import { hasAnyRole } from "@myapp/contract";

const ADMIN_ROLES: import('@myapp/contract').UserRole[] = ['MANAGER', 'DIRECTOR', 'SUPERUSER'];

export const WorkOrderDetailPage: React.FC = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const { data, isLoading, isError } = useWorkOrderDetail(workOrderId);

  const handleAddAppointment = () => {
    if (!workOrderId) return;
    const gewerk = data?.external.gewerkId ?? 0;
    navigate(routes.disposition.root + `?workOrderId=${workOrderId}&gewerk=${gewerk}`);
  };

  const isFollowUpRequired =
    data?.status === 'FOLLOW_UP_REQUIRED_1' || data?.status === 'FOLLOW_UP_REQUIRED_2';

  const isNewAppointmentAllowed =
    data?.external.isWohnungsmodernisierung ||
    isFollowUpRequired ||
    (!data?.external.isWohnungsmodernisierung && !data?.appointments?.length);
    
  const handlePlanFollowUp = () => {
    if (!workOrderId) return;
    const gewerk = data?.external.gewerkId ?? 0;
    navigate(routes.disposition.root + `?workOrderId=${workOrderId}&gewerk=${gewerk}&isFollowUp=true`);
  };

  const pageTitle = (
    <>
      Auftrag <em style={{ fontWeight: 400 }}>Nr. {workOrderId}</em>
      {data && (
        <span style={{ color: getWorkOrderStatusColor(data.status) }}>
          {" "}{getWorkOrderStatusLabel(data.status)}
        </span>
      )}
    </>
  );

  return (
    <PageLayout
      pageTitle={pageTitle}
      scrollable
      back
      backTo={routes.task.root}
      primaryAction={(data && isNewAppointmentAllowed) ? {
        label: isFollowUpRequired ? "Folgetermin planen" : "Neuer Termin",
        onClick: isFollowUpRequired ? handlePlanFollowUp : handleAddAppointment,
      } : undefined}

    >
      {isLoading && (
        <p className="mt-6 text-gray-400">Lade...</p>
      )}

      {isError && (
        <p className="mt-6 text-red-500">Auftrag konnte nicht geladen werden.</p>
      )}

      {data && (
        <div className="flex flex-col gap-[20px] mt-6 overflow-auto">
          <ImmotionAuftragSection
            immotionAuftragId={workOrderId ?? ""}
            readonly
          />
          <AppointmentsSection appointments={data.appointments} />
          {user && hasAnyRole(user.roles ?? [], ADMIN_ROLES) && workOrderId && (
            <AuditLogFeed workOrderId={workOrderId} />
          )}
        </div>
      )}
    </PageLayout>
  );
};
